import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';

import ArrModal from '../common/arr-modal/ArrModal';
import ArrButton from '../common/arr-button/ArrButton';
import ArrSingleSelectDropdown from '../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';

import {
  useChangeUserRole,
  useDomains,
  useProjectsRoleImpersonation,
  useSecurityGroupsRoleImpersonation,
  useUserRole,
} from '../../hooks/users/useUsers';

import {ISelectItem, RoleOption} from '../../shared/types';
import {RoleNames} from '../../shared/constants';
import {RootState} from '../../redux/store';
import {
  setShowRoleImpersonationModal,
  setRoleImpersonationPrefill,
  clearRoleImpersonationPrefill,
} from '../../redux/slice/roleImpersonationSlice';
import {setAuthenticatedUser} from '../../redux/slice/authenticatedUserSlice';
import './RoleImpersonation.scss';
import debounce from 'lodash/debounce';
import {
  IChangeUserRolePayload,
  IChangeUserRoleResponse,
} from '../../api/types/user.type';

const defaultEntityId = 0; // Default entity ID for System Administrator
const defaultProjectId = 0; // Default project ID for System Administrator

const RoleImpersonation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const showRoleImpersonationModal = useSelector(
    (state: RootState) => state.roleImpersonation.showRoleImpersonationModal
  );
  const prefill = useSelector(
    (state: RootState) => state.roleImpersonation.prefill
  );

  const [selectedRole, setSelectedRole] = useState<ISelectItem | null>(null);
  const [domain, setDomain] = useState<ISelectItem | null>(null);
  const [project, setProject] = useState<ISelectItem | null>(null);
  const [securityGroup, setSecurityGroup] = useState<ISelectItem | null>(null);
  // Controls whether persisted values (from loginUserData) should prefill domain/project/securityGroup
  const [prefillEnabled, setPrefillEnabled] = useState<boolean>(true);
  const [touchedFields, setTouchedFields] = useState({
    role: false,
    domain: false,
    project: false,
    securityGroup: false,
  });

  const {data: roles} = useUserRole();
  const applicableRoles = roles?.applicableRoles || [];

  const roleOptions: ISelectItem[] = useMemo(
    () => applicableRoles.map((r) => ({id: r.key, name: r.value})),
    [applicableRoles]
  );

  const role = selectedRole?.name as RoleOption | null;
  const roleId = typeof selectedRole?.id === 'number' ? selectedRole.id : -1;
  const projectId = typeof project?.id === 'number' ? project.id : -1;

  const showDomainDropdown =
    role === RoleNames.DomainAdministrator ||
    role === RoleNames.ProjectAdministrator ||
    role === RoleNames.ReviewManager ||
    role === RoleNames.Reviewer;

  const showProjectDropdown =
    role === RoleNames.ProjectAdministrator ||
    role === RoleNames.ReviewManager ||
    role === RoleNames.Reviewer;

  const showSecurityDropdown =
    role === RoleNames.ReviewManager || role === RoleNames.Reviewer;

  const shouldFetchDomains = showDomainDropdown;
  const shouldFetchProjects =
    (role === RoleNames.ProjectAdministrator ||
      role === RoleNames.ReviewManager ||
      role === RoleNames.Reviewer) &&
    !!domain;
  const shouldFetchSecurityGroups =
    (role === RoleNames.ReviewManager || role === RoleNames.Reviewer) &&
    !!project;

  const {data: fetchedDomains = []} = useDomains(
    roleId,
    loginUserData.entityId,
    {enabled: shouldFetchDomains && roleId !== -1}
  );

  const {data: fetchedProjects = []} = useProjectsRoleImpersonation(
    roleId,
    Number(domain?.id),
    {enabled: shouldFetchProjects && roleId !== -1 && !!domain}
  );

  const {data: availableSecurityGroups = []} =
    useSecurityGroupsRoleImpersonation(roleId, projectId, {
      enabled: shouldFetchSecurityGroups && roleId !== -1 && projectId !== -1,
    });

  const domainOptions = fetchedDomains.map((d) => ({id: d.key, name: d.value}));
  const projectOptions = fetchedProjects.map((p) => ({
    id: p.key,
    name: p.value,
  }));
  const securityGroupOptions = useMemo(
    () => availableSecurityGroups.map((s) => ({id: s.key, name: s.value})),
    [availableSecurityGroups]
  );

  const {mutate: submitRoleChange} = useChangeUserRole();

  // Debounced dispatcher for prefill updates
  const debouncedSetPrefill = React.useMemo(
    () =>
      debounce(
        (
          payload: Partial<{
            domain: ISelectItem | null;
            project: ISelectItem | null;
            securityGroup: ISelectItem | null;
          }>
        ) => dispatch(setRoleImpersonationPrefill(payload)),
        200
      ),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedSetPrefill.cancel();
    };
  }, [debouncedSetPrefill]);

  const resetAll = useCallback(() => {
    setSelectedRole(null);
    setDomain(null);
    setProject(null);
    setSecurityGroup(null);
    setTouchedFields({
      role: false,
      domain: false,
      project: false,
      securityGroup: false,
    });
  }, []);

  const handleClose = useCallback(() => {
    resetAll();
    dispatch(setShowRoleImpersonationModal(false));
  }, [dispatch, resetAll]);

  useEffect(() => {
    if (showRoleImpersonationModal) {
      // New modal session enables prefilling from persisted user data
      setPrefillEnabled(true);
      resetAll();
      if (loginUserData?.roleName && roleOptions.length > 0) {
        const matchedRole = roleOptions.find(
          (r) => r.name === loginUserData.roleName
        );
        if (matchedRole) setSelectedRole(matchedRole);
      }
    }
  }, [
    showRoleImpersonationModal,
    loginUserData?.roleName,
    roleOptions,
    resetAll,
  ]);

  // Populate domain dropdown with previously selected value
  useEffect(() => {
    if (
      showRoleImpersonationModal &&
      showDomainDropdown &&
      domainOptions.length > 0 &&
      !domain &&
      prefillEnabled
    ) {
      // Prefer prefill from header selection if present and valid
      if (prefill?.domain) {
        const matchedByPrefill = domainOptions.find(
          (d) => String(d.id) === String(prefill.domain?.id)
        );
        if (matchedByPrefill) {
          setDomain(matchedByPrefill);
          return;
        }
      }

      // Fallback to authenticated user's persisted selection
      if (loginUserData?.entityId) {
        const matchedDomain = domainOptions.find(
          (d) => d.id === loginUserData.entityId
        );
        if (matchedDomain) setDomain(matchedDomain);
      }
    }
  }, [
    showRoleImpersonationModal,
    showDomainDropdown,
    domainOptions,
    domain,
    prefillEnabled,
    prefill?.domain?.id,
  ]);

  // Populate project dropdown with previously selected value
  useEffect(() => {
    if (
      showRoleImpersonationModal &&
      showProjectDropdown &&
      projectOptions.length > 0 &&
      !project &&
      prefillEnabled
    ) {
      // Prefer prefill from header selection if present and valid
      if (prefill?.project) {
        const matchedByPrefill = projectOptions.find(
          (p) => String(p.id) === String(prefill.project?.id)
        );
        if (matchedByPrefill) {
          setProject(matchedByPrefill);
          setPrefillEnabled(false); // Only allow prefill once per modal open
          return;
        }
      }

      // Fallback to authenticated user's persisted selection
      if (loginUserData?.projectId) {
        const matchedProject = projectOptions.find(
          (p) => String(p.id) === String(loginUserData.projectId)
        );
        if (matchedProject) {
          setProject(matchedProject);
          setPrefillEnabled(false); // Only allow prefill once per modal open
        }
      }
    }
  }, [
    showRoleImpersonationModal,
    showProjectDropdown,
    projectOptions,
    project,
    prefillEnabled,
    prefill?.project?.id,
    loginUserData?.projectId,
  ]);

  // Sync project with header prefill changes (override when header changes)
  useEffect(() => {
    if (!showRoleImpersonationModal || !showProjectDropdown) return;
    if (!prefill?.project) return;

    const matchedByPrefill = projectOptions.find(
      (p) => String(p.id) === String(prefill.project?.id)
    );

    if (matchedByPrefill && project?.id !== matchedByPrefill.id) {
      setProject(matchedByPrefill);
    }
  }, [
    showRoleImpersonationModal,
    showProjectDropdown,
    prefill?.project?.id,
    projectOptions,
    project?.id,
  ]);

  // Helper function to determine which security group to select
  const getSecurityGroupToSelect = useCallback(() => {
    // For Review Manager, always try to auto-select the default security group (isSelected: true)
    if (
      role === RoleNames.ReviewManager &&
      availableSecurityGroups.length > 0
    ) {
      // If prefill is enabled, first try the user's current security group (persistence)
      if (prefillEnabled && loginUserData?.securityGroupId) {
        const matchedSecurityGroup = securityGroupOptions.find(
          (s) => s.id === loginUserData.securityGroupId
        );
        // Only use it if it's actually available for the current project
        if (matchedSecurityGroup) {
          return matchedSecurityGroup;
        }
      }

      // If only one security group is available, auto-select it
      if (securityGroupOptions.length === 1) {
        return securityGroupOptions[0];
      }

      // Otherwise, select the default security group
      const defaultSecurityGroup = availableSecurityGroups.find(
        (sg) => sg.isSelected === true
      );
      if (defaultSecurityGroup) {
        const defaultOption = securityGroupOptions.find(
          (s) => s.id === defaultSecurityGroup.key
        );
        return defaultOption;
      }
    }

    // For Reviewer role,  auto-select if only one security group is available
    if (role === RoleNames.Reviewer && securityGroupOptions.length === 1) {
      return securityGroupOptions[0];
    }

    // For other roles, try to match the user's current security group (for persistence)
    if (prefillEnabled && loginUserData?.securityGroupId) {
      const matchedSecurityGroup = securityGroupOptions.find(
        (s) => s.id === loginUserData.securityGroupId
      );
      if (matchedSecurityGroup) return matchedSecurityGroup;
    }

    return null;
  }, [
    loginUserData?.securityGroupId,
    securityGroupOptions,
    role,
    availableSecurityGroups,
    prefillEnabled,
  ]);

  // Populate security group dropdown with previously selected value
  // For Review Manager, auto-select default security group if no previous selection
  useEffect(() => {
    // Only auto-select when there's no valid security group selected
    if (
      showRoleImpersonationModal &&
      showSecurityDropdown &&
      securityGroupOptions.length > 0 &&
      (!securityGroup ||
        !securityGroupOptions.some((s) => s.id === securityGroup.id))
    ) {
      // Use setTimeout to ensure MenuItems are rendered before setting value
      const timer = setTimeout(() => {
        // Prefer prefill from header selection if present and valid
        if (prefill?.securityGroup) {
          const matchedByPrefill = securityGroupOptions.find(
            (s) => String(s.id) === String(prefill.securityGroup?.id)
          );
          if (matchedByPrefill) {
            setSecurityGroup(matchedByPrefill);
            return;
          }
        }

        const selectedGroup = getSecurityGroupToSelect();
        if (selectedGroup) {
          setSecurityGroup(selectedGroup);
        } else if (securityGroup) {
          setSecurityGroup(null);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [
    showRoleImpersonationModal,
    showSecurityDropdown,
    securityGroupOptions,
    availableSecurityGroups,
    securityGroup,
    getSecurityGroupToSelect,
    role,
    prefill?.securityGroup?.id,
  ]);

  // Sync security group object reference with current options to prevent MUI error
  useEffect(() => {
    if (securityGroup && securityGroupOptions.length > 0) {
      const matchingOption = securityGroupOptions.find(
        (opt) => opt.id === securityGroup.id
      );
      // Only update if actually needed
      if (!matchingOption && securityGroup !== null) {
        setSecurityGroup(null);
      }
    }
  }, [securityGroup, securityGroupOptions]);

  // Clear selections only when switching to System Administrator
  useEffect(() => {
    if (role === RoleNames.SystemAdministrator) {
      setPrefillEnabled(false);
      setDomain(null);
      setProject(null);
      setSecurityGroup(null);
      // Treat as fresh session; clear any prefill propagated from header
      dispatch(clearRoleImpersonationPrefill());
      setTouchedFields({
        role: true,
        domain: false,
        project: false,
        securityGroup: false,
      });
    }
  }, [role, dispatch]);

  // Only clear dependent selections when domain changes
  useEffect(() => {
    setProject(null);
    setSecurityGroup(null);
  }, [domain]);

  // When project changes, reset security group to allow auto-selection
  // The security group will be auto-selected by getSecurityGroupToSelect
  useEffect(() => {
    setSecurityGroup(null);
  }, [project?.id]);

  const isValid = useCallback((): boolean => {
    if (!role) return false;
    switch (role) {
      case RoleNames.SystemAdministrator:
        return true;
      case RoleNames.DomainAdministrator:
        return !!domain;
      case RoleNames.ProjectAdministrator:
        return !!domain && !!project;
      case RoleNames.ReviewManager:
      case RoleNames.Reviewer:
        return !!domain && !!project && !!securityGroup;
      default:
        return false;
    }
  }, [role, domain, project, securityGroup]);

  const handleBlur = (field: keyof typeof touchedFields): void => {
    setTouchedFields((prev) => ({...prev, [field]: true}));
  };

  const getValidationError = (field: keyof typeof touchedFields): string => {
    if (
      field === 'domain' &&
      touchedFields.domain &&
      showDomainDropdown &&
      !domain
    )
      return t('impersonationModule.domainErrorText');
    if (
      field === 'project' &&
      touchedFields.project &&
      showProjectDropdown &&
      !project
    )
      return t('impersonationModule.projectErrorText');
    if (
      field === 'securityGroup' &&
      touchedFields.securityGroup &&
      showSecurityDropdown &&
      !securityGroup
    )
      return t('impersonationModule.securityGroupErrorText');
    return '';
  };

  const handleSave = useCallback(() => {
    if (!isValid()) return;

    let payload: IChangeUserRolePayload = {
      role: Number(selectedRole?.id ?? -1),
    };

    const roleName = selectedRole?.name;
    const baseEntityId = Number(domain?.id ?? -1);
    const baseProjectId = Number(project?.id ?? -1);
    const baseSecurityGroupId = Number(securityGroup?.id ?? -1);

    switch (roleName) {
      case RoleNames.SystemAdministrator:
        payload = {
          ...payload,
          entityID: defaultEntityId,
          projectID: defaultProjectId,
        };
        break;
      case RoleNames.DomainAdministrator:
        payload = {...payload, entityID: baseEntityId};
        break;
      case RoleNames.ProjectAdministrator:
        payload = {
          ...payload,
          entityID: baseEntityId,
          projectID: baseProjectId,
        };
        break;
      case RoleNames.ReviewManager:
      case RoleNames.Reviewer:
        payload = {
          ...payload,
          entityID: baseEntityId,
          projectID: baseProjectId,
          securityGroupID: baseSecurityGroupId,
        };
        break;
      default:
        payload = {...payload, entityID: baseEntityId};
    }

    submitRoleChange(payload, {
      onSuccess: (data: IChangeUserRoleResponse) => {
        const apiData = data ?? data;
        if (!apiData) return;

        const mergedUser = {
          ...loginUserData,
          ...apiData,
          userName: apiData.userName ?? loginUserData.userName,
          emailID: apiData.emailID ?? loginUserData.emailID,
        };

        dispatch(setAuthenticatedUser(mergedUser));
        dispatch(setShowRoleImpersonationModal(false));
        resetAll();
        if (apiData.roleName) {
          navigate(apiData.landingPage);
        }
      },
      onError: (error: any) => {
        console.error('Role change failed', error);
      },
    });
  }, [
    isValid,
    submitRoleChange,
    selectedRole,
    domain,
    project,
    securityGroup,
    dispatch,
    resetAll,
    navigate,
    loginUserData,
  ]);
  const shortInfo = () => (
    <>
      <Typography
        className="typo-text"
        data-testid={`chane-role-info-text`}
        tabIndex={0}
      >
        {t('impersonationModule.changeRoleInfoText')}
      </Typography>
    </>
  );
  return (
    <ArrModal
      headerTitle={t('impersonationModule.impersonation')}
      onClose={handleClose}
      showModal={showRoleImpersonationModal}
      className="role-impersonation"
      dataTestId="role-impersonation-modal"
    >
      <Box className="change-role-box">
        <Typography
          className="typo-text headline3_size change-role"
          data-testid={`role-impersonation-change-role`}
          tabIndex={0}
        >
          {t('impersonationModule.changeRole')}
        </Typography>
        <ArrTooltipWrapper
          closeTooltipOnEscPress={true}
          title={t('impersonationModule.changeRole')}
          disablePortal={true}
          autoFocusCloseButton={true}
          trigger={
            <InfoIcon
              fontSize="medium"
              className="info-icon"
              tabIndex={0}
              aria-label="info-icon"
            />
          }
        >
          {shortInfo()}
        </ArrTooltipWrapper>
      </Box>
      <Box className="role-box-container">
        <Box className="my-role-box">
          <Typography
            className="typo-text body2_size my-role-text"
            data-testid={`role-impersonation-my-role`}
            tabIndex={0}
          >
            {t('impersonationModule.myRole')}
          </Typography>
          <Box className="role-text-box">
            <Typography
              className="typo-text label3_size role-text"
              data-testid={`role-impersonation-role-name`}
              tabIndex={0}
            >
              {loginUserData?.roleName}
            </Typography>
          </Box>
        </Box>
        <Typography
          className="typo-text body4_size required-text"
          tabIndex={0}
          data-testid={`role-impersonation-required-field-text`}
        >
          {t('impersonationModule.requiredFieldText')}
        </Typography>
      </Box>
      <ArrSingleSelectDropdown
        dropdownLabel={t('impersonationModule.impersonateTo')}
        dropdownItems={roleOptions}
        dropdownItemDisplay="name"
        dropdownItemKey="id"
        selectedValues={selectedRole}
        onSelectionChange={setSelectedRole}
        onBlur={() => handleBlur('role')}
        //searchable
        dataTestId="asd-role-impersonate-select-role"
      />
      {showDomainDropdown && (
        <Box className="dropdown-space">
          <ArrSingleSelectDropdown
            dropdownLabel={t('impersonationModule.domain')}
            dropdownItems={domainOptions}
            selectedValues={domain}
            onSelectionChange={(val) => {
              setDomain(val);
              // Propagate to header via prefill and reset dependent selections
              debouncedSetPrefill({
                domain: val,
                project: null,
                securityGroup: null,
              });
            }}
            dropdownItemDisplay="name"
            dropdownItemKey="id"
            dataTestId="asd-role-impersonate-select-domain"
            onBlur={() => handleBlur('domain')}
            error={!!getValidationError('domain')}
            errorText={getValidationError('domain')}
            searchable
            showNoResultsText
          />
        </Box>
      )}
      {showProjectDropdown && (
        <Box className="dropdown-space">
          <ArrSingleSelectDropdown
            dropdownLabel={t('impersonationModule.project')}
            dropdownItems={projectOptions}
            selectedValues={project}
            onSelectionChange={(val) => {
              setProject(val);
              // Propagate to header via prefill and reset security group selection
              debouncedSetPrefill({
                project: val,
                securityGroup: null,
              });
            }}
            dropdownItemDisplay="name"
            dropdownItemKey="id"
            dataTestId="asd-role-impersonate-select-project"
            onBlur={() => handleBlur('project')}
            error={!!getValidationError('project')}
            errorText={getValidationError('project')}
            searchable
            showNoResultsText
          />
        </Box>
      )}
      {showSecurityDropdown && (
        <Box className="dropdown-space">
          <ArrSingleSelectDropdown
            dropdownLabel={t('impersonationModule.securityGroup')}
            dropdownItems={securityGroupOptions}
            selectedValues={securityGroup}
            onSelectionChange={(val) => {
              setSecurityGroup(val);
              // Propagate to header via prefill
              debouncedSetPrefill({
                securityGroup: val,
              });
            }}
            dropdownItemDisplay="name"
            dropdownItemKey="id"
            dataTestId="asd-role-impersonate-security-group"
            onBlur={() => handleBlur('securityGroup')}
            error={!!getValidationError('securityGroup')}
            errorText={getValidationError('securityGroup')}
            searchable
            showNoResultsText
          />
        </Box>
      )}
      <Box className="action-container">
        <ArrButton
          variant="secondary"
          onClick={handleClose}
          className="btn-cancel"
          dataTestId="btn-cancel"
        >
          {t('impersonationModule.btnCancel')}
        </ArrButton>

        <ArrButton
          variant="primary"
          onClick={handleSave}
          disabled={!isValid()}
          className="btn-save"
          dataTestId="btn-save"
        >
          {t('impersonationModule.btnSave')}
        </ArrButton>
      </Box>
    </ArrModal>
  );
};

export default RoleImpersonation;
